/**
 * Moldura da aplicação: cabeçalho com navegação, conteúdo e rodapé.
 *
 * Envolve toda tela logada. O esboço trata **Agentes** e **Sistemas** como
 * seções irmãs no topo, não como filtro de uma lista só — e isso é decisão de
 * produto, não de estilo: são catálogos com contagens, abas e busca próprias.
 */
import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { api, photoUrl, SessionError } from '../api.js';
import { Avatar } from './Avatar.jsx';
import { Bell } from './Bell.jsx';
import { useTour } from '../tour/motor.jsx';
import { FeedbackDialog } from './FeedbackDialog.jsx';
import { Novidades, abrirNovidades } from './Novidades.jsx';
import { Icon } from './Icon.jsx';
import { useTheme } from '../hooks/useTheme.js';

/**
 * Iniciais a partir do nome, ou do e-mail quando nao ha nome.
 *
 * Exportado desde 21/10: o seletor de compartilhamento desenha pessoas e NAO
 * tem rota de foto para elas — `photoUrl.user` e da area administrativa.
 * Iniciais resolvem sem abrir endpoint novo, e duplicar seis linhas puras em
 * outro arquivo e como duas copias comecam a divergir.
 */
export function initialsOf(name, upn) {
  const nomeLimpo = String(name ?? '').trim();

  // Sem nome, o e-mail tem estrutura e vale a pena usar: `anderson.fernandes@`
  // dá AF, enquanto cortar os dois primeiros caracteres daria AN — igual ao de
  // `ana.sobrenome@`, que é outra pessoa. O domínio não entra.
  const base = nomeLimpo || String(upn ?? '').split('@')[0];
  const partes = base.split(/[\s._-]+/).filter(Boolean);

  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

// Gêmea de `iniciais` em `backend/src/routes/sites.js`: a moldura da página
// publicada e desenhada no servidor, sem o pacote do Vite, e precisa da mesma
// regra. Se uma mudar, a outra vai junto — senão a mesma pessoa aparece com
// iniciais diferentes em duas telas.

/* ── menu do usuário ─────────────────────────────────────────── */

function UserMenu({ me }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();
  const { abrir: abrirTour, disponiveis } = useTour();
  const { theme, choose } = useTheme();

  // Fecha ao clicar fora e ao teclar Esc. Sem isso o menu fica preso aberto,
  // e num cabeçalho fixo ele cobre o conteúdo.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClickOutside = (e) => {
      if (!boxRef.current?.contains(e.target)) setMenuOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  if (!me) return <div className="user-area" />;

  return (
    <div className="user-area" ref={boxRef}>
      <Bell />
      <Avatar src={photoUrl.me(me.oid)} initials={me.initials} name={me.name ?? me.upn} />

      <button
        type="button"
        className="user-name"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={() => setMenuOpen((v) => !v)}
      >
        Olá, <strong>{me.firstName}</strong>
        <span className="caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {menuOpen && (
        <div className="user-menu" role="menu">
          <div className="user-menu-meta">{me.upn}</div>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              navigate('/solicitacoes');
            }}
          >
            Minhas solicitações
          </button>

          {/* Só aparece para administrador. Isto NÃO é o controle de acesso —
              o servidor recusa `/api/admin` com 403 para qualquer outro, e a
              rota `/admin` existe no roteador para todo mundo. Esconder o item
              é cortesia: oferecer uma porta que vai bater na cara não ajuda
              ninguém. */}
          {me.isAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                navigate('/admin');
              }}
            >
              Admin
            </button>
          )}

          {/* ── os tours, numa seção própria ──────────────────────────────
              Esta é a porta PRINCIPAL dos tours: o convite do primeiro acesso
              é um atalho para cá, e não o contrário — tour que só roda uma vez
              é tour que ninguém viu.

              Em seção separada porque são de outra natureza: os itens acima
              LEVAM a algum lugar, estes ENSINAM onde você já está. Misturados,
              "Conhecer o AI Hub" parecia mais uma tela a visitar.

              A lista é filtrada por papel — oferecer o tour de administração a
              quem não administra seria prometer telas que a pessoa não abre. */}
          <div className="menu-section is-tours">
            <div className="menu-section-label">Tours guiados</div>
            {disponiveis(Boolean(me.isAdmin)).map((t) => (
              <button
                key={t.id}
                type="button"
                role="menuitem"
                className="menu-tour"
                onClick={() => {
                  setMenuOpen(false);
                  abrirTour(t.id);
                }}
              >
                <Icon name="compass" />
                <span>
                  {t.nome}
                  <span className="menu-tour-resumo">{t.resumo}</span>
                </span>
              </button>
            ))}
            {/* Reabrir o aviso de novidades fica JUNTO dos tours porque é da
                mesma natureza: não leva a lugar nenhum, conta o que mudou.
                E existe pela lição do tour — toda ação que fecha precisa vir
                com a que reabre, senão fechar é irreversível. */}
            <button
              type="button"
              role="menuitem"
              className="menu-tour"
              onClick={() => {
                setMenuOpen(false);
                abrirNovidades();
              }}
            >
              <Icon name="sparkles" />
              <span>
                O que há de novo
                <span className="menu-tour-resumo">As mudanças recentes da plataforma</span>
              </span>
            </button>
          </div>

          {/* Tres opcoes, e nao um interruptor de duas posicoes. "Sistema" e a
              ausencia de escolha: quem fica nele acompanha o computador
              sozinho, inclusive quando ele muda de tema ao anoitecer. Um
              interruptor obrigaria a pessoa a escolher um lado para sempre. */}
          <div className="menu-section">
            <div className="menu-section-label">Aparência</div>
            <div className="theme-picker" role="radiogroup" aria-label="Tema da interface">
              {[
                { id: 'light', label: 'Claro' },
                { id: 'dark', label: 'Escuro' },
                { id: 'sistema', label: 'Sistema' },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={theme === o.id}
                  className={`theme-option ${theme === o.id ? 'is-active' : ''}`}
                  onClick={() => choose(o.id)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Formulário, e não link: o logout é POST de propósito. Por GET,
              uma <img> numa página qualquer derrubaria a sessão de quem a
              visitasse. */}
          <form method="post" action="/auth/logout">
            <button type="submit" role="menuitem">
              Sair
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

/* ── moldura ─────────────────────────────────────────────────── */

export function AppShell({ children }) {
  const [me, setMe] = useState(null);
  const [feedback, setFeedback] = useState(false);

  // O sino aponta para uma conversa com `?mensagens=<id>`.
  //
  // A janela vive aqui, no rodapé que existe em TODA tela — então ela abre
  // sobre a página em que a pessoa já estava, sem rota nova e sem perder de
  // onde ela veio. `?mensagens=todas` abre só a lista: é o destino das
  // notificações antigas, que nunca guardaram o id da conversa.
  const [params, setParams] = useSearchParams();
  const mensagens = params.get('mensagens');

  useEffect(() => {
    if (mensagens) setFeedback(true);
  }, [mensagens]);

  // Fechar tira o parâmetro da URL — senão um F5 reabriria a janela, e voltar
  // no histórico faria o mesmo.
  function fecharFeedback() {
    setFeedback(false);
    if (!mensagens) return;
    const novos = new URLSearchParams(params);
    novos.delete('mensagens');
    setParams(novos, { replace: true });
  }

  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((d) => {
        if (alive) setMe(d);
      })
      .catch((err) => {
        // Sessão expirada aqui não redireciona: quem manda para o login é a
        // tela, que sabe para onde a pessoa queria ir. A moldura só desenha
        // sem o nome.
        if (!(err instanceof SessionError)) console.error(err);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-left">
          <NavLink to="/" className="wordmark">
            IGUATEMI
          </NavLink>
          {/*
            A ordem agrupa por natureza, e a barra entre os dois grupos é o que
            torna o agrupamento visível.

            **Agentes, Sistemas e Artefatos são acervos** — coisas que existem
            e se abrem. Os dois primeiros a casa oferece; o terceiro a pessoa
            publica, e por isso fecha o grupo em vez de abri-lo.

            **Solicitações é outra natureza:** não é um acervo, é o andamento
            dos pedidos de quem está olhando. Ficava no meio, entre Sistemas e
            Artefatos, separando duas coisas que são a mesma — e era a única
            que muda de conteúdo sozinha.

            Artefatos fica no topo, e não no menu do usuário, porque é uma
            seção com tela própria — como Agentes e Sistemas — e não um atalho
            para os "meus" de algo.
          */}
          <nav aria-label="Seções" data-tour="nav-secoes">
            <NavLink to="/" end className="topbar-tab">
              <Icon name="sparkles" />
              Agentes
            </NavLink>
            <NavLink to="/sistemas" className="topbar-tab">
              <Icon name="grid" />
              Sistemas
            </NavLink>
            <NavLink to="/artefatos" className="topbar-tab">
              <Icon name="window" />
              Artefatos
            </NavLink>

            {/* Decoração pura: o agrupamento que ela mostra já está na ordem,
                e um leitor de tela anunciando "separador" entre dois links não
                acrescenta nada ao que a sequência já diz. */}
            <span className="topbar-sep" aria-hidden="true" />

            <NavLink to="/solicitacoes" className="topbar-tab" data-tour="nav-solicitacoes">
              <Icon name="inbox" />
              Solicitações
            </NavLink>
          </nav>
        </div>
        <UserMenu
          me={me ? { ...me, initials: me.initials ?? initialsOf(me.name, me.upn) } : null}
        />
      </header>

      <div className="app-main">{children}</div>

      <footer className="app-footer">
        {/* Era "Entre em contato: ia@iguatemi.com.br" — a conta de serviço da
            equipe, não uma caixa de atendimento. Quem escrevesse para lá
            cairia num lugar que ninguém lê como suporte. */}
        <div className="footer-left">
          <button
            type="button"
            className="footer-link"
            data-tour="feedback"
            onClick={() => setFeedback(true)}
          >
            Dúvidas ou sugestões?
          </button>
          <span>A equipe de Inovação e IA responde por aqui.</span>
        </div>
        <div className="footer-right">
          <span>© 2026 Iguatemi S.A.</span>
          <span className="footer-brand">AI HUB</span>
        </div>
      </footer>

      {feedback && (
        <FeedbackDialog
          onClose={fecharFeedback}
          initialId={mensagens && mensagens !== 'todas' ? mensagens : null}
        />
      )}

      {/* O aviso de novidades mora aqui pela mesma razão da janela de
          mensagens: a moldura existe em TODA tela, então ele aparece onde a
          pessoa estiver sem precisar de rota própria.

          Só depois de `me` chegar — ele filtra por administrador, e montar
          antes mostraria a lista de usuário comum para quem administra. */}
      {me && <Novidades admin={Boolean(me.isAdmin)} />}
    </div>
  );
}

/* ── banner ──────────────────────────────────────────────────── */

/** Saudação pela hora do dia. O esboço mostra hora e dia da semana. */
function greeting() {
  const now = new Date();
  const h = now.getHours();
  const part = h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
  const day = now.toLocaleDateString('pt-BR', { weekday: 'long' });
  const hour = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${part}, são ${hour} de ${day}.`;
}

export function Banner({ title, children }) {
  return (
    <div className="banner">
      <div>
        <div className="banner-title">{title}</div>
        <div className="banner-subtitle">{children ?? greeting()}</div>
      </div>
      <div className="banner-ornament" aria-hidden="true" />
    </div>
  );
}

/* ── barra de números ────────────────────────────────────────── */

export function StatsBar({ items }) {
  return (
    <div className="stats-bar">
      {items.map((n) => (
        <div className="stat" key={n.label}>
          <div className="stat-label">{n.label}</div>
          <div className="stat-value">{n.value}</div>
        </div>
      ))}
    </div>
  );
}
